// IM-JRN-007 (IM-UI-090) — Catálogo de jornadas (/jornadas) e card condicional da Visão geral (IM-UX-108).
// A camada desligada responde 404: a página vira "Página não encontrada" e o card não ocupa espaço nenhum. As jornadas
// sem definição publicada aparecem só como "Em breve", sem ação. Iniciar exige ADMIN; a próxima ação leva à tela do módulo.
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { JOURNEY_STATE_LABEL, type JourneyCatalogItemDto, type JourneyDto, type JourneyView } from '@intelimove/contracts';
import { useCanManage } from './Targets';
import { NotFound } from './NotFound';
import { useSubmitOnce } from '../components/Recommendation';
import { AsyncView, ErrorState, ForbiddenState, LoadingState } from '../components/States';
import { useToast } from '../components/Toast';
import { PageHead, StatusPill } from '../components/Widgets';
import { fmtDate, isOff, journeysApi, LIFECYCLE_TONE, NEXT_KIND_LABEL } from '../data/journeys';
import { useResource } from '../hooks/useResource';
import { ApiError } from '../api/client';

const VIEWS: Array<[JourneyView, string]> = [['ativas', 'Ativas'], ['concluidas', 'Concluídas e canceladas'], ['arquivadas', 'Arquivadas']];

/** Progresso em texto (nunca só a barra): "n de m etapas obrigatórias". */
export function ProgressLine({ d }: { d: Pick<JourneyDto, 'progress'> }) {
  const p = d.progress;
  const text = `${p.requiredDone} de ${p.requiredApplicable} etapas obrigatórias concluídas`;
  return (
    <div className="jr-progress">
      <div className="progress" role="progressbar" aria-label="Progresso da jornada" aria-valuemin={0} aria-valuemax={100} aria-valuenow={p.percent ?? undefined} aria-valuetext={p.percent === null ? `${text}; percentual indisponível` : text}>
        <i style={{ width: `${p.percent ?? 0}%` }} />
      </div>
      <span className="cell-sub">{text}{p.percent !== null ? ` (${p.percent}%)` : ' — percentual indisponível: há etapa sem leitura'}{p.optionalTotal ? ` · ${p.optionalDone} de ${p.optionalTotal} opcionais` : ''}{p.stale ? ` · ${p.stale} desatualizada(s)` : ''}</span>
    </div>
  );
}

/** Resumo de uma instância viva: estado, progresso, próxima ação e atualização (uma leitura agregada). */
function LiveSummary({ id, compact = false }: { id: string; compact?: boolean }) {
  const { state, reload } = useResource((s) => journeysApi.get(id, s), [id]);
  return (
    <AsyncView state={state} onRetry={reload} empty={null}>
      {(d) => (
        <div className="jr-live">
          <p className="jr-live-head"><StatusPill tone={LIFECYCLE_TONE[d.state.state] ?? 'gray'}>{JOURNEY_STATE_LABEL[d.state.state]}</StatusPill> <span className="cell-sub inline">Atualizada em {fmtDate(d.journey.updatedAt)}</span></p>
          <ProgressLine d={d} />
          {d.nextAction && !compact && <p className="jr-next-line"><b>Próximo passo:</b> {NEXT_KIND_LABEL[d.nextAction.kind]} — {d.nextAction.title}</p>}
          {d.nextAction && compact && <p className="jr-next-line"><b>Próximo passo:</b> {d.nextAction.title}</p>}
          <Link className="cta btn-link" to={`/jornadas/${d.journey.id}`} aria-label={`Retomar ${d.journey.title}`}>Retomar</Link>
        </div>
      )}
    </AsyncView>
  );
}

function StartButton({ item, label = 'Iniciar jornada' }: { item: JourneyCatalogItemDto; label?: string }) {
  const { busy, run } = useSubmitOnce();
  const navigate = useNavigate();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <button className="cta" type="button" disabled={busy} aria-busy={busy} onClick={() => run(async () => {
        setError(null);
        try { const r = await journeysApi.create({ type: item.type }); navigate(`/jornadas/${r.id}`); if (!r.created) toast('A jornada já estava em andamento: você foi levado a ela.'); }
        catch (e) { setError(e instanceof ApiError ? e.message : 'Não foi possível iniciar agora.'); }
      })}>{busy ? 'Iniciando…' : label}</button>
      {error && <p className="field-error" role="alert">{error}</p>}
    </>
  );
}

function Available({ item }: { item: JourneyCatalogItemDto }) {
  const canManage = useCanManage();
  const orgLive = item.live[0] ?? null;
  return (
    <article className="card jr-type" aria-labelledby={`jt-${item.type}`}>
      <div className="card-head"><div><h2 id={`jt-${item.type}`}>{item.title}</h2><div className="card-sub">{item.objective}</div></div></div>
      <dl className="kv small jr-type-kv">
        <dt>Para começar</dt><dd>{item.needs}</dd>
        <dt>Ao final</dt><dd>{item.deliverable}</dd>
        <dt>Etapas</dt><dd>{item.steps.filter((s) => s.phase === 'CONDUCAO').length} etapas, com o que o InteliMove já tem</dd>
      </dl>
      {orgLive ? <LiveSummary id={orgLive.id} /> : item.state === 'PRONTA_PARA_COMECAR' ? (
        <div className="jr-start">
          <p><StatusPill tone="action">{JOURNEY_STATE_LABEL.PRONTA_PARA_COMECAR}</StatusPill></p>
          {canManage ? <StartButton item={item} /> : <p className="cell-sub">Um administrador da organização pode iniciar esta jornada.</p>}
        </div>
      ) : (
        <div className="jr-start">
          <p><StatusPill tone="gray">{JOURNEY_STATE_LABEL.NAO_INICIADA}</StatusPill> <span className="cell-sub inline">Falta o mínimo para começar:</span></p>
          <ul className="plain-list">{item.missing.map((m) => <li key={m.key}>{m.route ? <Link to={m.route}>{m.label}</Link> : m.label}</li>)}</ul>
        </div>
      )}
      {item.live.length > 1 && <p className="cell-sub">Há {item.live.length - 1} outra(s) em andamento com recorte — veja a lista abaixo.</p>}
    </article>
  );
}

function Instances() {
  const [params, setParams] = useSearchParams();
  const view = (VIEWS.map((v) => v[0]) as string[]).includes(params.get('visao') ?? '') ? params.get('visao') as JourneyView : 'ativas';
  const { state, reload } = useResource((s) => journeysApi.list(view, s), [view]);
  const label = VIEWS.find((v) => v[0] === view)![1];
  return (
    <section className="jr-instances" aria-labelledby="jr-inst">
      <h2 className="card-title" id="jr-inst">Suas jornadas</h2>
      <nav className="tabs" aria-label="Visões das jornadas">
        {VIEWS.map(([v, l]) => <a key={v} href={`?visao=${v}`} className={view === v ? 'active' : ''} aria-current={view === v ? 'page' : undefined} onClick={(e) => { e.preventDefault(); const p = new URLSearchParams(params); p.set('visao', v); setParams(p); }}>{l}</a>)}
      </nav>
      <AsyncView state={state} onRetry={reload} empty={null}>
        {(d) => d.items.length === 0 ? <div className="card notice" role="status">Nenhuma jornada em “{label}”.</div> : (
          <div className="table-card"><div className="table-wrap">
            <table className="data-table stack"><caption className="sr-only">Jornadas: {label}</caption>
              <thead><tr><th scope="col">Jornada</th><th scope="col">Situação</th><th scope="col">Atualizada</th><th scope="col"><span className="sr-only">Ações</span></th></tr></thead>
              <tbody>{d.items.map((x) => (
                <tr key={x.id}>
                  <td data-label="Jornada"><b>{x.title}</b>{x.ownerName && <span className="cell-sub">Responsável: {x.ownerName}</span>}</td>
                  <td data-label="Situação"><StatusPill tone={LIFECYCLE_TONE[x.lifecycle] ?? 'gray'}>{JOURNEY_STATE_LABEL[x.lifecycle]}</StatusPill></td>
                  <td data-label="Atualizada">{fmtDate(x.updatedAt)}</td>
                  <td><Link className="small-action" to={`/jornadas/${x.id}`} aria-label={`Abrir ${x.title}`}>Abrir</Link></td>
                </tr>
              ))}</tbody>
            </table>
          </div></div>
        )}
      </AsyncView>
    </section>
  );
}

export function Journeys() {
  const { state, reload } = useResource((s) => journeysApi.types(s), []);
  useEffect(() => { if (state.kind === 'ready') document.title = 'Jornadas · InteliMove'; }, [state.kind]);
  if (state.kind === 'error' && isOff(state.error)) return <NotFound />;
  if (state.kind === 'loading') return <LoadingState variant="grid" />;
  if (state.kind === 'forbidden') return <ForbiddenState />;
  if (state.kind === 'error') return <ErrorState error={state.error} onRetry={reload} />;
  if (state.kind !== 'ready') return null;
  const available = state.data.items.filter((x) => x.available);
  const soon = state.data.items.filter((x) => !x.available);
  return (
    <section aria-labelledby="page-title">
      <PageHead eyebrow="Jornadas" title="Jornadas" subtitle="Conduza um objetivo comercial do começo à decisão, usando o que o InteliMove já tem. Cada passo abre a tela de sempre." />
      <div className="jr-types">{available.map((x) => <Available key={x.type} item={x} />)}</div>
      {soon.length > 0 && (
        <p className="cell-sub jr-soon"><span className="tag gray">Em breve</span> {soon.map((x) => x.title).join(' · ')}</p>
      )}
      <Instances />
    </section>
  );
}

/** Card da Visão geral: só existe com a camada ligada; enquanto carrega ou se desligada, não ocupa espaço. */
export function JourneysOverviewCard() {
  const { state } = useResource((s) => journeysApi.types(s), []);
  if (state.kind !== 'ready') return null;
  const available = state.data.items.filter((x) => x.available);
  // IM-JRN-010: um único card (sem ocupar mais espaço): a jornada em andamento; senão a primeira pronta para começar.
  const pick = available.find((x) => x.live.length) ?? available.find((x) => x.state === 'PRONTA_PARA_COMECAR') ?? available[0];
  if (!pick) return null;
  const live = pick.live[0] ?? null;
  const others = available.length - 1;
  return (
    <article className="card jr-overview" style={{ marginBottom: 18 }} aria-labelledby="jr-ov">
      <div className="card-head"><div><h2 id="jr-ov">Jornada: {pick.title}</h2><div className="card-sub">Um caminho guiado sobre as análises que você já tem</div></div><Link className="link-btn" to="/jornadas">{others > 0 ? `Ver as ${available.length} jornadas` : 'Ver jornadas'}</Link></div>
      {live ? <LiveSummary id={live.id} compact /> : <OverviewStart item={pick} />}
    </article>
  );
}
function OverviewStart({ item }: { item: JourneyCatalogItemDto }) {
  const canManage = useCanManage();
  if (item.state !== 'PRONTA_PARA_COMECAR') return <p className="card-sub">Para começar: {item.needs} <Link to="/jornadas">Ver o que falta</Link>.</p>;
  return canManage ? <StartButton item={item} /> : <p className="card-sub">Pronta para começar. Um administrador pode iniciá-la em <Link to="/jornadas">Jornadas</Link>.</p>;
}
